import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Clock, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-[#D9D2E8] bg-white px-4 py-3 text-sm text-[#2B1B45] placeholder:text-[#7A7390]/60 focus:outline-none focus:ring-2 focus:ring-[#F2644C]/40 focus:border-[#F2644C] transition-all duration-300";

const initialForm = {
  name: "",
  email: "",
  phone: "",
  due_date: "",
  service: "",
  message: "",
};

const ContactSection = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        due_date: form.due_date || null,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Thank you. Your enquiry has been received.", {
        description: "Adonica will reach out to you personally within 1–2 business days.",
      });
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry.", {
        description: "Please try again, or call 209-425-5979 directly.",
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 lg:py-28 bg-[#2E1B4D] text-white relative overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 h-[30rem] w-[30rem] rounded-full bg-[#F2644C]/20 blur-3xl" />
      <div className="absolute -bottom-40 -right-40 h-[30rem] w-[30rem] rounded-full bg-[#2563EB]/20 blur-3xl" />
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 relative">
        <div className="grid lg:grid-cols-5 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-2"
          >
            <p className="text-xs uppercase tracking-[0.25em] font-heading font-bold text-[#F2644C] mb-4">
              Begin the conversation
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-6">
              Start your{" "}
              <span className="font-serif italic font-semibold text-[#F5B301]">
                maternal health
              </span>{" "}
              journey
            </h2>
            <p className="text-base text-[#CFC7E6] leading-relaxed mb-10">
              Request a free 15-minute intro consultation to discuss your care
              plan. Share a little about yourself and where you are. Adonica
              will personally follow up.
            </p>
            <div className="space-y-5">
              <a
                data-testid="contact-phone-text"
                href="tel:+12094255979"
                className="flex items-center gap-3 text-[#CFC7E6] hover:text-white transition-colors group w-fit"
              >
                <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#F2644C] transition-colors">
                  <Phone className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">209-425-5979</span>
              </a>
              <a
                data-testid="contact-email-placeholder"
                href="mailto:hello@sageandseabirth.com"
                className="flex items-center gap-3 text-[#CFC7E6] hover:text-white transition-colors group w-fit"
              >
                <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center group-hover:bg-[#F2644C] transition-colors">
                  <Mail className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">hello@sageandseabirth.com</span>
              </a>
              <div className="flex items-center gap-3 text-[#CFC7E6]">
                <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                  <MapPin className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">
                  Tracy, CA, serving all of San Joaquin County
                </span>
              </div>
              <div className="flex items-center gap-3 text-[#CFC7E6]">
                <span className="h-10 w-10 rounded-full bg-white/10 flex items-center justify-center">
                  <Clock className="h-4 w-4" />
                </span>
                <span className="text-sm font-semibold">
                  Replies within 1–2 business days
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            data-testid="enquiry-contact-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
            className="lg:col-span-3 bg-white rounded-2xl p-7 sm:p-10 shadow-2xl"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  Your name
                </label>
                <input
                  id="contact-name"
                  data-testid="contact-input-name"
                  required
                  value={form.name}
                  onChange={set("name")}
                  placeholder="Jordan Rivera"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  Email
                </label>
                <input
                  id="contact-email"
                  data-testid="contact-input-email"
                  type="email"
                  required
                  value={form.email}
                  onChange={set("email")}
                  placeholder="you@example.com"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-phone" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  Phone <span className="normal-case font-medium">(optional)</span>
                </label>
                <input
                  id="contact-phone"
                  data-testid="contact-input-phone"
                  type="tel"
                  value={form.phone}
                  onChange={set("phone")}
                  placeholder="(209) 555-0123"
                  className={inputCls}
                />
              </div>
              <div>
                <label htmlFor="contact-due-date" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  Estimated due date
                </label>
                <input
                  id="contact-due-date"
                  data-testid="contact-input-due-date"
                  type="date"
                  value={form.due_date}
                  onChange={set("due_date")}
                  className={inputCls}
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-service" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  What are you interested in?
                </label>
                <select
                  id="contact-service"
                  data-testid="contact-select-service"
                  value={form.service}
                  onChange={set("service")}
                  className={inputCls}
                >
                  <option value="">Choose a service…</option>
                  <option>Full-Spectrum Doula Care</option>
                  <option>Childbirth Education</option>
                  <option>Maternal Health Coaching</option>
                  <option>Postpartum Support</option>
                  <option>Not sure yet, help me decide</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className="block text-xs font-heading font-bold uppercase tracking-wider text-[#5B5470] mb-2">
                  Tell me a little about your journey
                </label>
                <textarea
                  id="contact-message"
                  data-testid="contact-input-message"
                  required
                  rows={4}
                  value={form.message}
                  onChange={set("message")}
                  placeholder="Where are you in your pregnancy or postpartum journey? What kind of support feels right?"
                  className={`${inputCls} resize-none`}
                />
              </div>
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={sending}
              className="mt-7 inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-[#F2644C] text-white text-sm font-bold tracking-wide hover:bg-[#D9523B] disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
              {sending ? "Sending…" : "Send enquiry"}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
